/**
 * Ziele und Erfolge.
 *
 * Zwei Ansichten mit einer Aufgabe: dem Spieler sagen, was ein sinnvoller
 * nächster Schritt wäre – und warum. Der Begründungstext ist deshalb kein
 * Beiwerk, sondern der eigentliche Inhalt: Er erklärt, weshalb ein zweiter
 * Upstream im echten Betrieb ein Meilenstein ist, und macht die Kampagne
 * nebenbei zum Tutorial.
 *
 * Gesperrte Kampagnenziele werden gezeigt, aber ohne Begründung. Der Titel
 * verrät die Richtung, der Text bleibt die Belohnung fürs Ankommen.
 */
import { useEffect, useMemo, useState } from 'react';
import { tickToDate, type ObjectiveView } from '@hosting-sim/core';
import { Badge, Card, EmptyState, Meter, PageHeader, Spinner, StatGrid, Stat, Tabs, type Tone } from '../components/ui';
import { gameDay } from '../lib/format';
import { api } from '../lib/api';
import { useGame, useMetrics } from '../store/game';

const AREA_LABEL: Record<ObjectiveView['area'], string> = {
  growth: 'Wachstum',
  network: 'Netz',
  operations: 'Betrieb',
  finance: 'Finanzen',
};

const AREA_TONE: Record<ObjectiveView['area'], Tone> = {
  growth: 'success',
  network: 'info',
  operations: 'neutral',
  finance: 'warning',
};

export function Objectives() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  // Der Tick ist der Auslöser für ein Nachladen: Ziele werden einmal pro
  // Spieltag ausgewertet, häufiger zu fragen bringt nichts. Der Spieltag lässt
  // sich aus dem Tick ablesen, ohne dass der Server etwas mitschicken müsste.
  const dayIndex = Math.floor((metrics?.meta.tick ?? 0) / 24);

  const [items, setItems] = useState<ObjectiveView[] | null>(null);
  const [tab, setTab] = useState<'campaign' | 'achievement'>('campaign');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    api
      .objectives(gameId)
      .then((data) => {
        if (!cancelled) setItems(data.objectives);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId, dayIndex]);

  const campaign = useMemo(() => items?.filter((o) => o.kind === 'campaign') ?? [], [items]);
  const achievements = useMemo(() => items?.filter((o) => o.kind === 'achievement') ?? [], [items]);

  const campaignDone = campaign.filter((o) => o.completedAtTick !== null).length;
  const achievementsDone = achievements.filter((o) => o.completedAtTick !== null).length;
  const current = campaign.find((o) => o.available && o.completedAtTick === null) ?? null;

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Ziele & Erfolge" />
        <EmptyState title="Die Ziele konnten nicht geladen werden." description={error} />
      </div>
    );
  }

  if (!items) {
    return (
      <div className="space-y-4">
        <PageHeader title="Ziele & Erfolge" />
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ziele & Erfolge"
        description="Der rote Faden durch den Aufbau – und was darüber hinaus geht."
      />

      <StatGrid cols={3}>
        <Stat label="Kampagne" value={`${campaignDone} / ${campaign.length}`} hint="abgeschlossene Meilensteine" />
        <Stat
          label="Erfolge"
          value={`${achievementsDone} / ${achievements.length}`}
          hint="freigeschaltet"
          tone={achievementsDone > 0 ? 'positive' : 'neutral'}
        />
        <Stat label="Als Nächstes" value={current?.title ?? 'Kampagne abgeschlossen'} hint={current?.progress.label} />
      </StatGrid>

      <Tabs
        active={tab}
        onChange={(id) => setTab(id as typeof tab)}
        tabs={[
          { id: 'campaign', label: 'Kampagne', badge: `${campaignDone}/${campaign.length}` },
          { id: 'achievement', label: 'Erfolge', badge: `${achievementsDone}/${achievements.length}` },
        ]}
      />

      {tab === 'campaign' ? (
        <div className="space-y-2">
          {campaign.map((objective) => (
            <ObjectiveRow key={objective.id} objective={objective} isCurrent={objective.id === current?.id} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {achievements.map((objective) => (
            <AchievementCard key={objective.id} objective={objective} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Eine Stufe der Kampagne. */
function ObjectiveRow({ objective, isCurrent }: { objective: ObjectiveView; isCurrent: boolean }) {
  const done = objective.completedAtTick !== null;
  const locked = !objective.available && !done;

  return (
    <Card
      className={
        isCurrent
          ? 'border-accent-500/60 ring-1 ring-accent-500/30'
          : locked
            ? 'opacity-55'
            : undefined
      }
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <span
          className={
            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ' +
            (done
              ? 'bg-accent-500 text-white'
              : locked
                ? 'bg-panel-200 text-panel-400 dark:bg-panel-800 dark:text-panel-600'
                : 'border border-accent-500 text-accent-600 dark:text-accent-400')
          }
          aria-hidden
        >
          {done ? '✓' : locked ? '🔒' : '›'}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{objective.title}</h3>
            <Badge tone={AREA_TONE[objective.area]}>{AREA_LABEL[objective.area]}</Badge>
            {isCurrent && <Badge tone="info">Als Nächstes</Badge>}
          </div>

          {/* Der Lerntext ist die Belohnung fürs Freischalten – gesperrt bleibt er verdeckt. */}
          {locked ? (
            <p className="mt-1 text-xs text-panel-500 dark:text-panel-400">
              Wird freigeschaltet, sobald die vorherigen Ziele erreicht sind.
            </p>
          ) : (
            <p className="mt-1 text-sm text-panel-600 dark:text-panel-300">{objective.rationale}</p>
          )}

          {!locked && (
            <div className="mt-3">
              <Meter
                value={objective.progress.current}
                max={objective.progress.target}
                label={
                  <>
                    <span>{objective.progress.label}</span>
                    {done && objective.completedAtTick !== null && (
                      <span className="text-accent-600 dark:text-accent-400">
                        erreicht am {gameDay(tickToDate(objective.completedAtTick))}
                      </span>
                    )}
                  </>
                }
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Ein Erfolg – kompakter, weil er keine Reihenfolge hat. */
function AchievementCard({ objective }: { objective: ObjectiveView }) {
  const done = objective.completedAtTick !== null;

  return (
    <Card className={done ? 'border-accent-500/50' : 'opacity-80'}>
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold">{objective.title}</h3>
          <Badge tone={done ? 'success' : 'muted'}>{done ? 'Erreicht' : AREA_LABEL[objective.area]}</Badge>
        </div>
        <p className="text-xs text-panel-500 dark:text-panel-400">{objective.rationale}</p>
        <Meter
          value={objective.progress.current}
          max={objective.progress.target}
          label={
            <>
              <span>{objective.progress.label}</span>
              {done && objective.completedAtTick !== null && <span>{gameDay(tickToDate(objective.completedAtTick))}</span>}
            </>
          }
        />
      </div>
    </Card>
  );
}
